import { atom } from 'jotai'

import { type UpdateDispatchAndShipmentFormValue } from '@/features/vehicle-dispatch-boards/models'
import { type OrderTime } from '@/features/vehicle-dispatch-boards/utils'
import { type VehicleOrderStatusType } from '@/libs/api/types'

export interface DispatchStatusConfirmationModalState {
  isOpen: boolean
  statusLabel: string
  message: string
  dispatchId: number | null
  currentStatus: VehicleOrderStatusType | null
  targetStatus: VehicleOrderStatusType | null
  targetStatusLabel: string
  // 配車情報
  pendingDispatchInfo: {
    shipmentOrderIds: number[]
    startAt: string
    endAt: string
    phoneNumber: string | null
    // 再配車用の情報
    isReschedule: boolean
    /**
     * 新規配車（isReschedule: false）の着地先 Dispatch ID。着地確定後の startAt の
     * 日付から引き直した値（resolveDispatchIdByDate）。トップレベルの dispatchId
     * （承認済みロックの対象 Dispatch。ステータス遷移・再配車の targetDispatchId に
     * 使う。板の基準日固定のまま=既存挙動）とは別に持つ。再配車では未設定
     */
    scheduleDispatchId?: number
    /**
     * ドロップ先の行が日付ごとに持つ Dispatch の id 全件。同じ行の別日の配車に載る束ね
     * （前日から伸びた矢羽根）への合流先探索に使う。新規配車・再配車の両方で使う
     */
    targetRowDispatchIds?: number[]
    originalDispatchDetailId?: number
    /** 再配車時の楽観ロック用 updatedAt（detail 層1） */
    originalDetailUpdatedAt?: string
    /**
     * 複数依頼ドロップの依頼ごと時間制限補正結果（新規配車のみ）。
     * 指定時は startAt/endAt（envelope）の代わりに該当依頼の時刻を使う
     */
    orderTimes?: OrderTime[]
    /**
     * 時間制限判定用の配送依頼一覧（再配車のみ）。承認済み確認モーダルの確認後、
     * 再配車 mutation の直前で時間制限外かどうかを判定するために保持する
     * （新規配車は Phase 1 の自動補正で常に制限内になるため不要）。
     */
    shipmentOrdersForRestrictionCheck?: {
      departureTimeRestrictionStart: string | null
      departureTimeRestrictionEnd: string | null
      arrivalTimeRestrictionStart: string | null
      arrivalTimeRestrictionEnd: string | null
    }[]
    /**
     * ドロップ元が日モードだったか（isReschedule: false のときのみ意味を持つ）。
     * ステータス遷移後に新規配車が確定した際の横スクロール追従を、日モード
     * （列詰めレイアウト・時刻軸なし）ではスキップするための判定に使う。
     * 再配車（isReschedule: true）では読み取られないため未設定でよい
     */
    isDayMode?: boolean
  } | null
  // 編集内容の保存
  pendingEditFormValue: UpdateDispatchAndShipmentFormValue | null
  pendingEditContext: {
    tenantKey: string
    dispatchDetailId: number
    /** 所属する DispatchGroup。ドライバー3点の書き込み先（PUT /dispatch-groups の groups[].id） */
    dispatchGroupId: number
    /** 層1 楽観ロック（DispatchGroup 行） */
    groupUpdatedAt: string
    /** 層1 楽観ロック（DispatchDetail 行） */
    detailUpdatedAt: string
    onSuccess: () => Promise<void>
  } | null
}

export const dispatchStatusConfirmationModalAtom =
  atom<DispatchStatusConfirmationModalState>({
    isOpen: false,
    statusLabel: '',
    message: '',
    dispatchId: null,
    currentStatus: null,
    targetStatus: null,
    targetStatusLabel: '',
    pendingDispatchInfo: null,
    pendingEditFormValue: null,
    pendingEditContext: null
  })
